<!--
  NavCountBadge - a nav-tab count pill that never moves the tabs beside it.

  Measured on qa with the pill in normal flow: a count arriving pushed the four
  tabs after Messages 32px right, and a second digit moved them another 7px. The
  count is pushed over SignalR, so that happened with no user input at all and
  could slide a tab out from under the cursor mid-click.

  Two earlier attempts and why they were wrong:
   - A fixed-width reserved slot held the tabs still but left visible dead space
     inside the tab and stretched the active underline past the label.
   - Anchoring the pill to the tab's right edge (right-0.5) grew it leftward, so
     two- and three-character counts sat on top of the label text.

  What works: a zero-width anchor that stays in flow where the pill used to be,
  with the pill absolutely positioned out of it. The anchor is always rendered so
  the row's geometry never changes, it contributes no width at any digit count,
  and the pill grows rightward from the end of the label into the tab's own right
  padding, which is empty anyway. "99+" needs ~30px and there is ~40px of padding
  and inter-tab gap to grow into before the next tab's icon.
-->
<script setup lang="ts">
import { computed } from 'vue';
import type { HTMLAttributes } from 'vue';

const props = defineProps<{
    count: number;
    // Passed through so each caller keeps the pill styling it had.
    class?: HTMLAttributes['class'];
}>();

const label = computed(() => (props.count > 99 ? '99+' : String(props.count)));
</script>

<template>
    <span class="relative w-0 shrink-0">
        <!-- pointer-events-none so the pill can never swallow a click meant for
             the tab it is sitting on top of. -->
        <Badge
            v-if="count > 0"
            variant="secondary"
            :class="[
                'pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-[17px] min-w-[17px] justify-center px-1 text-[10px] font-bold leading-none',
                props.class,
            ]"
        >{{ label }}</Badge>
    </span>
</template>
